import { apiSlice } from "./apiSlice";
import fallbackMenus from "./fallbackRestaurantMenus.json";
import {
  buildFallbackRestaurantPage,
  FALLBACK_CURSOR_PREFIX,
} from "./fallbackRestaurantListing";

/* ---------------------------------------------------------------
   Restaurant endpoints

   GET /restaurants/:id/menu answers
     { success, data: { restaurantId, isSample, menuOf, categories } }
   and the components receive `data` directly.

   `isSample` is true while the backend only holds one real menu: the
   dishes then belong to `menuOf`, not to the restaurant on screen.
----------------------------------------------------------------*/

// Menus rarely change; keep a visited menu cached for five minutes so
// going back and forth between restaurants does not refetch.
const MENU_CACHE_SECONDS = 300;

// Give up on a hung backend and use the fallback instead of spinning.
const API_TIMEOUT_MS = 8000;

/* ---------------------------------------------------------------
   TEMPORARY fallback — remove once the backend is deployed.

   fallbackRestaurantMenus.json is a copy of the backend's seed file
   (server/src/database/seed/restaurantMenus.json), and
   buildFallbackMenu() mirrors the backend's getRestaurantMenu(), so
   the page gets exactly the response the API would have sent.

   The listing has the same arrangement: fallbackRestaurants.json plus
   fallbackRestaurantListing.js, a port of the backend's listing logic.

   To remove: delete the two fallback JSON files,
   fallbackRestaurantListing.js and the code in this block, then in
   both queryFns return `{ error: result.error }` instead of building
   a fallback (or swap them for plain `query` + `transformResponse`).
----------------------------------------------------------------*/

const SAMPLE_MENU_RESTAURANT_ID = "47120";

function buildFallbackMenu(restaurantId) {
  const byId = (id) => fallbackMenus.menus.find((m) => m.restaurant.id === id);
  const ownMenu = byId(restaurantId);
  const menu = ownMenu ?? byId(SAMPLE_MENU_RESTAURANT_ID);

  return {
    restaurantId,
    isSample: !ownMenu,
    menuOf: menu.restaurant,
    categories: menu.categories,
    // Not part of the API contract; lets the page (and you) tell the
    // copy from a live response while the fallback exists.
    isFallback: true,
  };
}

// The backend answered on purpose (bad id, no menu): show that, do not
// paper over it. Anything else — unreachable, timed out, 5xx, or a
// non-API page such as the host's 404 — means "backend not available".
const isDeliberateApiError = (error) =>
  typeof error.status === "number" &&
  error.status < 500 &&
  error.data?.success === false;

/* ---------------------------------------------- end TEMPORARY block */

const menuUrl = (restaurantId) =>
  `/restaurants/${encodeURIComponent(restaurantId)}/menu`;

// The listing changes faster than menus (ratings, delivery times).
const LISTING_CACHE_SECONDS = 60;

// Query-string params without the unset ones, in a stable key order, so
// the same filters always produce the same URL and the same cache key.
const cleanParams = (params) =>
  Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== "")
      .sort(([a], [b]) => a.localeCompare(b)),
  );

export const restaurantApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getRestaurantMenu: build.query({
      async queryFn(restaurantId, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: menuUrl(restaurantId),
          timeout: API_TIMEOUT_MS,
        });

        if (!result.error) return { data: result.data.data };
        if (isDeliberateApiError(result.error)) return { error: result.error };

        console.warn(
          "[menu] backend unavailable, using the bundled fallback menu",
          result.error.status,
        );
        return { data: buildFallbackMenu(restaurantId) };
      },
      keepUnusedDataFor: MENU_CACHE_SECONDS,
    }),

    /* GET /restaurants — the home page listing, with "Load more".
       Args: { q, city, cuisine, pureVeg, minRating, maxDeliveryMinutes,
               minCostForTwoPaise, maxCostForTwoPaise, sort, limit, cursor } */
    listRestaurants: build.query({
      async queryFn(args, _api, _extraOptions, baseQuery) {
        const params = cleanParams(args);

        // A page that started from the bundled copy continues from it.
        if (params.cursor?.startsWith(FALLBACK_CURSOR_PREFIX)) {
          return { data: buildFallbackRestaurantPage(params) };
        }

        const result = await baseQuery({
          url: "/restaurants",
          params,
          timeout: API_TIMEOUT_MS,
        });

        if (!result.error) return { data: result.data.data };
        if (isDeliberateApiError(result.error)) return { error: result.error };

        // Mid-way through the API's pages, the bundled copy cannot carry
        // on from an API cursor without repeating restaurants: surface the
        // error and let "Load more" be retried instead.
        if (params.cursor) return { error: result.error };

        console.warn(
          "[restaurants] backend unavailable, using the bundled fallback listing",
          result.error.status,
        );
        return { data: buildFallbackRestaurantPage(params) };
      },

      // One cache entry per filter set: the cursor is left out of the key,
      // so "Load more" appends to the same entry instead of starting a new one.
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { cursor, ...filters } = queryArgs;
        return `${endpointName}(${JSON.stringify(cleanParams(filters))})`;
      },
      merge: (cached, incoming, { arg }) => {
        if (!arg.cursor) return incoming; // first page (or a refresh) replaces
        cached.restaurants.push(...incoming.restaurants);
        cached.nextCursor = incoming.nextCursor;
        cached.total = incoming.total;
        cached.facets = incoming.facets;
        return cached;
      },
      // A new cursor on the same filters is a new request, not a cache hit.
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.cursor !== previousArg?.cursor,
      keepUnusedDataFor: LISTING_CACHE_SECONDS,
    }),
  }),
});

export const { useGetRestaurantMenuQuery, useListRestaurantsQuery } = restaurantApi;
